import { FormLabel, Heading, Radio, RadioList, Text } from "@oysterhr/ui"
import { BaseSyntheticEvent } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation, Trans } from "react-i18next"
import { NumericTextInput } from "@oysterhr/feature-components"
import { isCountrySpain } from "../../utils/helpers"
import { CalculationVAT, VATOptions } from "../../types"
import { RADIO_VALUE_NO, RADIO_VALUE_YES, VAT_RADIO_OPTIONS } from "../../utils/constants"

export type CalculationVATFormValues = { countryCode: string } & CalculationVAT

export interface CalculationVATFormProps {
  vatOptions: VATOptions
  countryCode: string
}

export const CalculationVATForm = ({ vatOptions, countryCode }: CalculationVATFormProps) => {
  /* Hooks */
  const { t } = useTranslation("cost_calculator_vat/CostCalculatorVATPage")

  const isSpain = isCountrySpain(countryCode)

  const requiresWorkInCountryOfResidence = vatOptions?.requiresWorksInCountryOfResidence
  const requiresSalesToCountry = vatOptions?.requiresSalesToCountry
  const requiresSalesPercentageToCountry = vatOptions?.requiresSalesPercentageToCountry
  const countryName = vatOptions?.countryName

  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext()

  const vatServices = useWatch({
    control,
    name: "serviceWithinCountry",
  })

  const handleVatServiceChange = (e: BaseSyntheticEvent) => {
    const value = e.target.value
    setValue("serviceWithinCountry", value)
    if (value === RADIO_VALUE_YES && requiresSalesToCountry) {
      setValue("salesWithinCountry", "")
      setValue("salesPercentage", "")
    }
  }

  /* Fields visibility */
  const showPercentageField = vatServices === RADIO_VALUE_NO && requiresSalesPercentageToCountry
  const showCompanyVATFields =
    showPercentageField || (vatServices === RADIO_VALUE_NO && requiresSalesToCountry)
  const showSalesField = !isSpain && showCompanyVATFields

  if (!requiresWorkInCountryOfResidence) return null

  return (
    <div className="space-y-4 ">
      <div className="flex flex-col space-y-4 pt-2">
        <Text size="large" className="font-semibold">
          {t("vatInformation")}
        </Text>
        <div>
          <div className="flex space-x-2">
            <Controller
              control={control}
              name="serviceWithinCountry"
              render={({ field: { name }, fieldState }) => (
                <RadioList
                  label={t("form.label.service", { countryName })}
                  orientation="horizontal"
                  onChange={handleVatServiceChange}
                  name={name}
                  error={fieldState.error?.message}
                >
                  {VAT_RADIO_OPTIONS.map(vatOption => (
                    <Radio key={vatOption.value} value={vatOption.value} label={vatOption.label} />
                  ))}
                </RadioList>
              )}
            />
          </div>
          {typeof errors.serviceWithinCountry?.message === "string" && (
            <span className="text-sm text-error-base">{errors.serviceWithinCountry?.message}</span>
          )}
        </div>
      </div>
      {showCompanyVATFields && (
        <div className={"flex flex-col space-y-4"}>
          <Heading size="lg">{t("vatInfoHeader")}</Heading>
          {showSalesField && (
            <div className="flex flex-col space-y-4">
              <div>
                <div className="flex space-x-2">
                  <Controller
                    control={control}
                    name="salesWithinCountry"
                    render={({ field: { name, onChange }, fieldState }) => (
                      <RadioList
                        label={t("form.label.sales", { countryName })}
                        orientation="horizontal"
                        onChange={onChange}
                        name={name}
                        error={fieldState.error?.message}
                      >
                        {VAT_RADIO_OPTIONS.map(vatOption => (
                          <Radio
                            key={vatOption.value}
                            value={vatOption.value}
                            label={vatOption.label}
                          />
                        ))}
                      </RadioList>
                    )}
                  />
                </div>
                {typeof errors.salesWithinCountry?.message === "string" && (
                  <span className="text-sm text-error-base">
                    {errors.salesWithinCountry?.message}
                  </span>
                )}
              </div>
            </div>
          )}
          {showPercentageField && (
            <div className="flex flex-col space-y-4">
              <FormLabel label={t("form.label.percentage", { countryName })} isOptional />
              <NumericTextInput
                type="number"
                maxDecimalsDigits={2}
                min={0}
                max={100}
                step={0.01}
                placeholder="0%"
                {...register("salesPercentage")}
              />
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-300">
        <Trans
          defaults={t("footer", { countryName })}
          components={{
            span: <span />,
          }}
        />
      </p>
    </div>
  )
}
